<script setup lang="ts">
import { Combobox } from '@ark-ui/vue'
import UiIcon from '../UiIcon.vue'
import UiAvatar from '../UiAvatar.vue'
import UiAsyncComboboxMiniChips from './UiAsyncComboboxMiniChips.vue'

interface Props {
    displayMode: 'inline' | 'combobox'
    multiple: boolean
    size: 'md' | 'lg'
    isInvalid: boolean
    isDisabled: boolean
    isOpen: boolean
    placeholder: string
    comboboxValue: string[]
    inputValue: string
    describedByIdBase: string | undefined
    validationError: string | undefined
    hint?: string
    getLabel: (value: string) => string
    showSelectedImage: boolean
    selectedAvatar?: {
        image?: string
        initials?: string
    }
}

const props = withDefaults(defineProps<Props>(), {
    showSelectedImage: false,
})

const emit = defineEmits<{
    (e: 'clear'): void
    (e: 'remove', value: string): void
    (e: 'blur'): void
}>()
</script>

<template>
    <Combobox.Control 
        class="w-full flex items-center gap-2 ring-1 ring-inset rounded-lg transition-colors"
        :class="{
            'min-h-10 py-2 px-4': props.size === 'md',
            'py-2.5 px-6': props.size === 'lg',
            'ring-se-danger': props.isInvalid,
            'ring-se-gray-300 hover:ring-se-gray-400 focus-within:ring-se-gray-500': !props.isInvalid,
            'cursor-pointer': props.displayMode === 'combobox',
        }"
    >
        <slot name="leading-icon" />
        
        <UiAvatar
            v-if="props.showSelectedImage && (props.selectedAvatar?.image || props.selectedAvatar?.initials)"
            :url="props.selectedAvatar?.image"
            :initials="props.selectedAvatar?.initials"
            size="xxxs"
            class="flex-shrink-0"
        />
        
        <!-- Mini chips for combobox mode multi-select -->
        <UiAsyncComboboxMiniChips
            v-if="props.displayMode === 'combobox' && props.multiple"
            :combobox-value="props.comboboxValue"
            :get-label="props.getLabel"
            @remove="emit('remove', $event)"
        />
        
        <Combobox.Input 
            :placeholder="props.placeholder" 
            class="flex-1 text-md bg-transparent outline-none"
            :class="{
                'w-0': props.displayMode === 'combobox' && props.multiple && props.comboboxValue.length > 0
            }"
            autocomplete="off"
            :disabled="props.isDisabled"
            :aria-invalid="props.isInvalid ? 'true' : undefined"
            :aria-describedby="props.validationError ? `${props.describedByIdBase}-error` : (props.hint ? `${props.describedByIdBase}-hint` : undefined)"
            @blur="emit('blur')"
        />
        
        <!-- Clear button for inline mode -->
        <button
            v-if="props.displayMode === 'inline' && props.inputValue"
            @click.stop="emit('clear')"
            type="button"
            class="cursor-pointer flex items-center justify-center w-5 h-5 rounded-full bg-se-gray-300 hover:bg-se-gray-400 transition-colors"
            aria-label="Clear search"
        >
            <UiIcon name="close" class="w-4 h-4 text-se-gray-700" />
        </button>
        
        <!-- Trigger for combobox mode -->
        <Combobox.Trigger v-if="props.displayMode === 'combobox'" as-child>
            <button type="button" class="flex items-center justify-center transition-transform" :class="{ 'rotate-180': props.isOpen }">
                <UiIcon name="chevron-down" class="w-5 h-5 text-se-gray-700" />
            </button>
        </Combobox.Trigger>
    </Combobox.Control>
</template>

